import { Box, Flex, Text } from '@chakra-ui/react';
import { useState } from 'react';
import EmptyBoardMemberReviews from './EmptyBoardMemberReviews';
import InviteExpertsModal from './InviteExperts';
import { useQuery } from '@tanstack/react-query';
import { fetchExpertsInviteDetails } from 'src/api/queries';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import {
  Button,
  ButtonIcon,
  Menu,
  MenuButton,
  MenuList,
  Table
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import ListLoader from 'src/components/ListLoader';
import { ISubmissionReviewer } from '@publishing-engine/domain/models';
import {
  assessmentBadges,
  reviewStatusBadges
} from 'src/components/Review/Badges';
import { formatDeadlineDate } from 'src/utils/date';
import ViewAssessmentModal from './ViewAssessmentModal';
import { IEarlyAssessmentListItem } from '@publishing-engine/domain/views/early-assessment';
import { ISubmissionWFState } from '@publishing-engine/domain/types/workflow-phases';
import SubmittedAuthorDetailsModal from '../AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';
import { renderMenuItem } from '../ReviewTab/TableColumns';
import { getReviewerNameOrEmail } from 'src/utils/reviews';
import EarlyAssessmentsInPeerReviewPhase from './EarlyAssessmentsInPeerReviewPhase';
import {
  checkOverdueDeadlineMessage,
  MAX_DAYS_TO_EXPIRE_DEADLINE
} from 'src/utils/deadlines';
import ProtectedWorkflowAction from '../../ProtectedAction/ProtectedWorkflowAction';
import ComposeEmailModal, {
  AdHocModalOpen
} from '../AuthorsTab/ComposeEmailModal';

const EarlyAssessmentsTab = () => {
  const [isInviteExpertsModalOpen, setIsInviteExpertsModalOpen] =
    useState(false);

  const [selectedExpert, setSelectedExpert] =
    useState<ISubmissionReviewer | null>(null);

  const [selectedViewAssessment, setSelectedViewAssessment] =
    useState<IEarlyAssessmentListItem | null>(null);

  const {
    submissionContextId,
    submissionId,
    workflowResponse,
    isTaskCompleter
  } = useSubmissionData();
  const [openSendEmailModal, setOpenSendEmailModal] = useState<AdHocModalOpen>({
    status: false,
    defaultEmail: []
  });
  const submissionCurrentState = workflowResponse.workflowData
    .currentState as ISubmissionWFState;

  const initialAssessmentStatus =
    submissionCurrentState === 'initialAssessment';

  const { data, isLoading } = useQuery({
    queryKey: [
      'board-member-experts-invite-list',
      { submissionContextId, submissionId }
    ],
    queryFn: () =>
      submissionId && submissionContextId
        ? fetchExpertsInviteDetails(
            submissionContextId,
            submissionId,
            'early-assessments'
          )
        : null
  });
  if (isLoading) {
    return <ListLoader />;
  }
  return (
    <>
      {isInviteExpertsModalOpen && (
        <InviteExpertsModal
          closeModal={() => setIsInviteExpertsModalOpen(false)}
        />
      )}
      {selectedExpert?.id ? (
        <SubmittedAuthorDetailsModal
          backButtonText="Back to Reviews"
          userId={selectedExpert?.id ?? ''}
          onBackClick={() => setSelectedExpert(null)}
          isProxyUserNotRequired={true}
        />
      ) : null}
      {selectedViewAssessment ? (
        <ViewAssessmentModal
          assessmentData={selectedViewAssessment}
          onClose={() => setSelectedViewAssessment(null)}
          type="Early Assessment"
        />
      ) : null}
      {!initialAssessmentStatus ? (
        <EarlyAssessmentsInPeerReviewPhase data={data} />
      ) : (
        <Flex flexDirection="column" marginTop={6} minWidth="100%">
          <Flex gap={2} flexDirection={'column'}>
            <Text textStyle="headingSection">Early Assessments</Text>
            <p>
              {data?.total ?? '0'} Experts Invited:{' '}
              {data?.completedAssessment ?? '0'} Assessments Completed,{' '}
              {data?.inProgress ?? '0'} In Progress,{' '}
              {data?.declinedOrExpired ?? '0'} Declined/Expired
            </p>
            {data && data?.total !== 0 && initialAssessmentStatus && (
              <Box marginTop={4}>
                <ProtectedWorkflowAction actionName="RequestEarlyAssessment">
                  <Button
                    onClick={() => setIsInviteExpertsModalOpen(prev => !prev)}
                  >
                    Invite Experts
                  </Button>
                </ProtectedWorkflowAction>
              </Box>
            )}
          </Flex>
          {data && data?.total === 0 ? (
            <EmptyBoardMemberReviews
              onInviteClick={() => setIsInviteExpertsModalOpen(prev => !prev)}
              title="No Experts Yet Invited"
              subtext="Invite a board member to assess the Submission, provide early
          recommendations and help you decide next steps."
              buttonText="Invite Experts"
              renderInviteCallToAction={initialAssessmentStatus}
              workflowAction="RequestEarlyAssessment"
            />
          ) : (
            <>
              {openSendEmailModal.status && (
                <ComposeEmailModal
                  setOpenSendEmailModal={setOpenSendEmailModal}
                  email={openSendEmailModal.defaultEmail}
                />
              )}
              <Box width="100%" marginTop={8} overflowX="auto">
                <Box minWidth="520px">
                <Table>
                  <Table.HeaderRow padding={2}>
                    <Table.HeaderCell flex="2">Expert</Table.HeaderCell>
                    <Table.HeaderCell flex="1">Status</Table.HeaderCell>
                    <Table.HeaderCell flex="1">Deadline</Table.HeaderCell>
                    <Table.HeaderCell flex="1">Recommendation</Table.HeaderCell>
                    <Table.HeaderCell width="44px"></Table.HeaderCell>
                  </Table.HeaderRow>
                  <Table.Body sx={{ borderTop: 'none' }}>
                    {data?.items.map((item, index) => {
                      const deadlineDate = item.workflow.currentDeadline
                        ? new Date(item.workflow.currentDeadline)
                        : null;
                      const { deadlineMessage, todaysDate, daysToDeadline } =
                        checkOverdueDeadlineMessage(
                          deadlineDate,
                          item.workflow.currentState
                        );

                      return (
                        <>
                          <Table.Row
                            key={index}
                            style={{
                              cursor:
                                item.workflow.currentState === 'reviewed' &&
                                !!item?.caseData?.assessment
                                  ? 'pointer'
                                  : 'default'
                            }}
                            onClick={() =>
                              item.workflow.currentState === 'reviewed' &&
                              !!item?.caseData?.assessment &&
                              setSelectedViewAssessment(item ?? null)
                            }
                          >
                            <Table.RowCell flex="2" textAlign="left">
                              {item?.expert ? (
                                <Flex flexDirection="column">
                                  <Text textStyle="headingItem">
                                    {getReviewerNameOrEmail(item?.expert)}
                                  </Text>
                                  <Text textStyle="textContext">
                                    {
                                      item?.expert?.affiliationDetails
                                        ?.organizationName
                                    }
                                  </Text>
                                </Flex>
                              ) : (
                                'N/A'
                              )}
                            </Table.RowCell>
                            <Table.RowCell flex="1">
                              {reviewStatusBadges[item.workflow.currentState] ||
                                reviewStatusBadges.firstAssessment}
                            </Table.RowCell>
                            <Table.RowCell flex="1">
                              {item.workflow?.currentDeadline !== null &&
                              item.workflow?.currentDeadline ? (
                                <Flex flexDirection="column">
                                  <Text
                                    textStyle="headingItem"
                                    color={
                                      (deadlineDate &&
                                        todaysDate >= deadlineDate) ||
                                      daysToDeadline <=
                                        MAX_DAYS_TO_EXPIRE_DEADLINE
                                        ? 'actionSecondaryCautionContent'
                                        : ''
                                    }
                                  >
                                    {formatDeadlineDate(
                                      item.workflow.currentDeadline
                                    )}
                                  </Text>
                                  {item.workflow?.currentDeadline && (
                                    <Text>{deadlineMessage}</Text>
                                  )}
                                </Flex>
                              ) : (
                                'N/A'
                              )}
                            </Table.RowCell>
                            <Table.RowCell flex="1">
                              {item?.caseData?.assessment
                                ? assessmentBadges[
                                    item.caseData.assessment?.recommendation
                                  ]
                                : 'N/A'}
                            </Table.RowCell>
                            <Table.RowCell
                              padding={2}
                              width="44px"
                              key="actions"
                            >
                              <div style={{ position: 'relative' }}>
                                <Menu ariaLabel="Actions">
                                  <MenuButton
                                    as={ButtonIcon}
                                    onClick={event => {
                                      event.stopPropagation();
                                    }}
                                    icon={<FontAwesomeIcon icon={faEllipsis} />}
                                    color="transparent"
                                  />
                                  <MenuList
                                    style={{
                                      position: 'absolute',
                                      zIndex: 1000,
                                      top: '48px'
                                    }}
                                  >
                                    {item.workflow.currentState ===
                                      'reviewed' &&
                                      !!item?.caseData?.assessment &&
                                      renderMenuItem('View Assessment', () =>
                                        setSelectedViewAssessment(item ?? null)
                                      )}

                                    {renderMenuItem('View Expert Profile', () =>
                                      setSelectedExpert(item.expert)
                                    )}
                                    {isTaskCompleter &&
                                      renderMenuItem('Send Email', () =>
                                        setOpenSendEmailModal({
                                          status: true,
                                          defaultEmail: [item.expert.email]
                                        })
                                      )}
                                  </MenuList>
                                </Menu>
                              </div>
                            </Table.RowCell>
                          </Table.Row>
                        </>
                      );
                    })}
                  </Table.Body>
                </Table>
                </Box>
              </Box>
            </>
          )}
        </Flex>
      )}
    </>
  );
};
export default EarlyAssessmentsTab;
